import { useRequiredContext } from "@redotech/react-util/context";
import GiftIcon from "@redotech/redo-icons/gift-01_filled.svg";
import HeartIcon from "@redotech/redo-icons/heart_filled.svg";
import StarIcon from "@redotech/redo-icons/star-01_filled.svg";
import TrophyIcon from "@redotech/redo-icons/trophy-01.svg";
import {
  HeroBlock,
  HeroBlockLogoType,
} from "@redotech/redo-model/loyalty/loyalty-page-builder/definition";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  LoyaltyPageContext,
  LoyaltyPageEnvironment,
} from "../context/loyalty-page-context";

export const Hero = memo(function Hero({
  copy,
  background,
  logoType,
  headerFontFamily,
  bodyFontFamily,
}: HeroBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const isLoggedIn = !!context.customer;
  const isBuilder = context.environment === LoyaltyPageEnvironment.BUILDER;
  const content = isLoggedIn ? copy.loggedIn : copy.loggedOut;

  return (
    <Flex
      align="center"
      dir="column"
      justify="center"
      style={{ position: "relative", width: "100%", overflow: "hidden" }}
    >
      {/* Background color */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          backgroundColor: background.fallbackColor,
        }}
      />
      {/* Background image */}
      {background.imageUrl && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            backgroundImage: `url(${background.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: background.imageOpacity,
          }}
        />
      )}

      {/* Content */}
      <Flex
        align="center"
        dir="column"
        gap="xl"
        justify="center"
        px="xl"
        py="6xl"
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "800px",
        }}
        textColor="white"
      >
        <HeroIcon logoType={logoType} />

        <Text
          fontWeight="bold"
          style={{
            fontSize: 32,
            fontFamily: headerFontFamily,
            color: content.titleColor,
          }}
          textAlign="center"
        >
          {content.title || (isBuilder ? "Placeholder title" : "")}
        </Text>

        <Text
          fontSize="lg"
          style={{ fontFamily: bodyFontFamily, color: content.subtitleColor }}
          textAlign="center"
        >
          {content.subtitle ||
            (isBuilder
              ? "Placeholder subtitle. Customize this text in the right sidebar."
              : "")}
        </Text>
      </Flex>
    </Flex>
  );
});

const HeroIcon = memo(function HeroIcon({
  logoType,
  size = 64,
}: {
  logoType: HeroBlockLogoType;
  size?: number;
}) {
  const context = useRequiredContext(LoyaltyPageContext);
  const logoUrl = context.teamSettings.brandKit?.images.logoUrl;

  switch (logoType) {
    case HeroBlockLogoType.TROPHY:
      return <TrophyIcon height={size} width={size} />;
    case HeroBlockLogoType.STAR:
      return <StarIcon height={size} width={size} />;
    case HeroBlockLogoType.GIFT:
      return <GiftIcon height={size} width={size} />;
    case HeroBlockLogoType.HEART:
      return <HeartIcon height={size} width={size} />;
    case HeroBlockLogoType.COMPANY_LOGO:
      return logoUrl ? (
        <img
          alt="Company Logo"
          height={size}
          src={logoUrl}
          style={{ maxWidth: `${size}px`, maxHeight: `${size}px` }}
          width={size}
        />
      ) : null;
    case HeroBlockLogoType.NONE:
      return null;
    default:
      assertNever(logoType);
  }
});
